import React from 'react';
import styled from 'styled-components';

import { Circle } from '../../styles/mainStyle';
import CheckIcon from '../../svgs/CheckIcon';
import { PhotoArtifactTab } from '../../types.ts/story';
import AddIconWrapper from '../common/AddIconWrapper';
import EnlargedImageRenderProp from '../common/EnlargedImageRenderProp';
import {
  AuthorAttribution,
  UnsplashAttribution,
} from '../common/UnsplashAttributions';

import type { PhotoAsset } from '../../types.ts/story';

interface PhotoGridProps {
  resource: PhotoAsset[] | undefined;
  tab: PhotoArtifactTab;
  selectedId?: string;
  unsplashSearch?: string;
  unsplashLoading?: boolean;
  onSelectResource: (resource: PhotoAsset, tab: PhotoArtifactTab) => void;
  onLoadMore?: () => void;
}

const SMALL_PHOTO_HEIGHT = 100;

const PhotoGrid: React.FC<PhotoGridProps> = ({
  resource,
  tab,
  selectedId,
  unsplashSearch,
  unsplashLoading,
  onSelectResource,
  onLoadMore,
}) => {
  const renderAssetLevelAttribution = (asset: PhotoAsset) => {
    if (!asset.attribution) return null;

    switch (tab) {
      case PhotoArtifactTab.stock:
        return (
          <AuthorAttribution
            author={asset.attribution.author}
            link={asset.attribution.link}
          />
        );
      default:
        return null;
    }
  };

  const renderTabLevelAttribution = () => {
    if (!resource?.some(a => a.attribution)) return null;

    switch (tab) {
      case PhotoArtifactTab.stock:
        return (
          <AttributionRow>
            <UnsplashAttribution />
          </AttributionRow>
        );
      default:
        return null;
    }
  };

  if (!resource) {
    return (
      <Container>
        <NoResource>
          Search the stock library to add photos to the story
        </NoResource>
      </Container>
    );
  }

  if (!resource.length) {
    return (
      <Container>
        <NoResource>You have no matching assets</NoResource>
      </Container>
    );
  }

  return (
    <Container>
      {resource.map(r => (
        <ResourceElementWrapper key={r.id}>
          <EnlargedImageRenderProp
            src={r.src}
            smallPhotoHeight={SMALL_PHOTO_HEIGHT}
          >
            {({ enlargedState, setEnlargedState, renderEnlargedImage }) => (
              <>
                <AddIconWrapper
                  onClick={() => onSelectResource(r, tab)}
                  customStyles={{
                    borderRadius: '10px',
                    border: '2px solid transparent',
                    transition: 'border-color var(--transition-fast) ease',
                  }}
                  customStylesWhenHovered={{
                    borderColor: 'var(--primary)',
                  }}
                  onMouseEnter={e => {
                    setEnlargedState(e.currentTarget.getBoundingClientRect());
                  }}
                  onMouseMove={e => {
                    if (!enlargedState) {
                      setEnlargedState(e.currentTarget.getBoundingClientRect());
                    }
                  }}
                  onMouseLeave={() => {
                    setEnlargedState(null);
                  }}
                >
                  <ResourceElement>
                    <Circle isSelected={selectedId === r.id}>
                      {selectedId === r.id && <CheckIcon />}
                    </Circle>
                    <ResourceImage loading="lazy" srcSet={r.src} />
                  </ResourceElement>
                </AddIconWrapper>
                {renderAssetLevelAttribution(r)}
                {enlargedState && renderEnlargedImage()}
              </>
            )}
          </EnlargedImageRenderProp>
        </ResourceElementWrapper>
      ))}
      {renderTabLevelAttribution()}
      {tab === PhotoArtifactTab.stock &&
        unsplashSearch &&
        resource.length > 0 && (
          <LoadMoreButton disabled={unsplashLoading} onClick={onLoadMore}>
            Load more photos
          </LoadMoreButton>
        )}
    </Container>
  );
};

export default PhotoGrid;

const Container = styled.div`
  margin-top: 16px;
  width: 100%;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  row-gap: 5px;
  column-gap: 16px;
`;

const ResourceElementWrapper = styled.div`
  &:not(:last-child) {
    margin-bottom: 10px;
  }
`;

const ResourceElement = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  cursor: pointer;
  height: ${SMALL_PHOTO_HEIGHT}px;
`;

const ResourceImage = styled.img`
  margin: auto;
  min-height: ${SMALL_PHOTO_HEIGHT}px;
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: var(--radius);
  object-position: top;
  box-shadow:
    0 2px 4px rgba(0, 0, 0, 0.1),
    0 4px 8px rgba(0, 0, 0, 0.1);
`;

// Empty-state pattern, rail variant: --text-xs / --text-muted. The wide-view
// EmptyText (Home/RecentContent, StoryManagerFolderView/DetailsPanel) is
// --text-sm, but at 14px this copy wraps in a 318px rail and leaves a one-word
// orphan line; at 12px it fits on a single line. This previously set neither
// size nor colour and inherited the ambient size.
const NoResource = styled.div`
  margin-top: 10px;
  grid-column: 1/3;
  text-align: center;
  font-size: var(--text-xs);
  color: var(--text-muted);
`;

const LoadMoreButton = styled.button`
  grid-column: 1/3;
  display: flex;
  padding: 12px 16px;
  justify-content: center;
  align-items: center;
  gap: 8px;
  border-radius: 10px;
  background-color: var(--background);
  color: var(--primary);
  border: 1px solid var(--primary);
  cursor: pointer;
  font-weight: var(--font-weight-bold);
  font-size: var(--text-sm);
  outline: 0;
  transition: var(--transition-normal);
  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

const AttributionRow = styled.div`
  grid-column: 1/3;
  margin: 6px 0 16px 0;
  display: flex;
  justify-content: center;
`;
